<template>
    <div class="Journaling">
        <div v-if="isLoading" class="content-container">
            <div class="loading">
                <span class="loader"></span>
            </div>
        </div>
        <div v-else class="content-container">
            <div class="top-content">
                <div class="flex-row-centered">
                    <MarketSelector @change=updateMarketSelection enableSE shortTitle/>
                    <div class="flex-col-centered selection">
                        <label for="owner"><h3 class="header">Owner:</h3></label>
                        <select v-model="ownerSelection" class="dropdown" required id="owner" @change="RefreshJournalingTable()">
                            <option v-for="contractOwner in contractOwners"
                                :value="contractOwner.OwnerId" 
                                :key="contractOwner.OwnerId" >
                                {{ contractOwner.FullName }}
                            </option>
                        </select>
                    </div>
                    <TitledSelection title="Journal Type Selection:" for="journalTypeSelect">
                        <select id="journalTypeSelect" v-model="journalingTypeSelection" class="dropdown" style="width: 16rem;" @change="journalingSelectionChanged()">
                            <option v-for="journalingType in journalingTypeOptions" :value="journalingType.val"
                                :key="journalingType.val">
                                {{ journalingType.text }}
                            </option>
                        </select>
                    </TitledSelection>
                    <div v-if="journalingTypeSelection !=''">
                        <TitledSelection for="forced-ids" :title="`Enter IDs to force journal:`">
                                <div style="display: grid; grid-template-columns: max-content;">
                                    <input v-model=forcedIDs
                                        id="forced-ids"
                                        type="text"
                                        placeholder="Enter IDs..."
                                        style="grid-column: 1; grid-row: 1; width: 100%;"/>
                                </div>
                        </TitledSelection>
                    </div>
                    <TitledSelection for="journal-ref-id" title="Search for Journal Reference:">
                        <form id="journal-ref" @submit.prevent= GetJournalingReports(true);>
                            <GroupedInputButton>
                                <input id="journal-ref-id"
                                v-model="journalReference"
                                class="selection-form-input"
                                type="text"
                                placeholder="Enter Journal Reference..."
                                pattern=".*\S+.*"
                            />
                            <button title="Enter exactly 1 journal reference to search" class="centre-button-journal-reference">
                                <MagnifyingGlass class="selection-form-btn" style="width: 20px;"></MagnifyingGlass>
                            </button>
                            </GroupedInputButton>
                        </form>
                    </TitledSelection>
                    <button @click="ProformaJNL()" class="centre-button" :disabled="tableError ||
                        proformaButtonDisabled ||
                        DenyFunctionLevelAccess(FunctionAccessLevels.ProformaJNL)
                        "
                        :title="DenyFunctionLevelAccess(FunctionAccessLevels.ProformaJNL) ? 'Proforma Journal (Access denied)' : 'Proforma Journal'">
                        Proforma JNL
                    </button>
                    <button @click="ExportJNL()" class="centre-button" :disabled="tableError ||
                        exportButtonDisabled ||
                        DenyFunctionLevelAccess(FunctionAccessLevels.ExportJNL)
                        "
                        :title="DenyFunctionLevelAccess(FunctionAccessLevels.ExportJNL) ? 'Export Journal (Access denied)' : 'Export Journal'">
                        Export JNL
                    </button>
                    <button @click="redirectToManualJournal" class="centre-button" :title="manualJNLButtonTitle" :disabled="DenyFunctionLevelAccess(FunctionAccessLevels.ManualJNL)">
                        Manual JNL
                    </button>
                </div>
            </div>
            <Table
                :Rows=displayedJournalingReports
                :Cols=cols
                :Labels=labels
                :RowClickedCallBack=RowClicked
                :DefaultSortCols=defaultSortCols
                :DefaultSortOrders=defaultSortOrders
                :HasError=tableError
                v-model:Page=page
                v-model:RowsPerPage=rowCount
            >
                <template #Status="props">
                    <div v-if="props.value.Status === 'SUCCESS'" class="highlight-green-bold">
                        {{ props.value.Status }}
                    </div>
                    <div v-if="props.value.Status === 'FAILURE'" class="failure">
                        {{ props.value.Status }}
                    </div>
                </template>
                <template #JournalType="props">
                    {{ CanonicalToCamel(props.value.JournalType) }}
                </template>
            </Table>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    Ref,
    onMounted,
    onBeforeUnmount,
    onBeforeMount,
    inject
} from "vue";
import { onBeforeRouteLeave } from "vue-router";
import router from "@/router";
// Services
import { apiLoggerKey } from "@/types/ServiceKeys";
import { authService } from "@/auth/authSingleton";
import { FunctionAccessLevels, DenyFunctionLevelAccess } from "@/config/AuthConfig";
import { MarketSelection, financialSystemNetworkService } from "@/services/helpers/Helper";
import {
    cols,
    labels,
    ParseItems,
    JournalingServices,
    ICache,
} from "@/services/helpers/JournalingHelper";
// Data
import IJournalReportsItem from "@/interfaces/Journaling/IJournalingReportsItem";
import { journalingTypes, JournalingType, CanonicalToCamel } from "@/models/Journaling/JournalingTypes";
import { ISelection } from "@/Interfaces/ISelection";
import { IMetadata } from "@/interfaces/IMetadata";
import IJournalingBody from "@/Interfaces/Journaling/IJournalingBody";
import { marketSelection, ownerSelection } from "@/services/helpers/Helper";
import GroupedInputButton from "@/components/GroupedInputButton.vue";
// Table
import Table from "@/components/TablePersistentChecking.vue";
// Misc
import log from "loglevel";
import MarketSelector from "@/components/MarketSelector.vue";
import TitledSelection from "@/components/TitledSelection.vue";
import { RoutePaths } from "@/router/RouteDetails";
import MagnifyingGlass from "@/components/svg/MagnifyingGlass.vue";
import { ContractOwner } from "@/models/CarPurchases/ContractOwner";
import { contractOwners as contractOwnersNetworkService } from "@/services/network";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";

const cacheHolder = ref<ICache>();
const contractOwners = ref<ContractOwner[]>([]);
const unfilteredContractOwners = ref<ContractOwner[]>([]);
const page = ref(1);
const rowCount = ref(50);
const defaultSortCols = ref([0]);
const defaultSortOrders = ref<(1|-1)[]>([-1]);
const scrollPosition = ref(0);
const forcedIDs = ref('');
const journalReference = ref('')
const manualJNLButtonTitle = ref(DenyFunctionLevelAccess(FunctionAccessLevels.ManualJNL)? "You don't have the required permission level to perform this action." : "Insert Manual Journal")
const fileName = "Journaling.vue";
const Logger = inject(apiLoggerKey);
const tableError = ref(false);
const proformaButtonDisabled = ref(false);
const exportButtonDisabled = ref(false);
const isLoading = ref(true);
const journalingReports = ref<IJournalReportsItem[]>();
const displayedJournalingReports = ref<IJournalReportsItem[]>();
const journalingServices: JournalingServices = new JournalingServices();
const lastProformaJobId= ref<number>();
const lastExportJobId = ref<number>();
const journalingTypeOptions: ISelection[] = journalingTypes.map((journalingType: JournalingType) => {
    return { text: journalingType.DisplayShort, val: journalingType.Canonical } as ISelection;
})
journalingTypeOptions.sort((a: ISelection, b: ISelection) =>
    a.text.localeCompare(b.text)
);
journalingTypeOptions.unshift({ text: "All", val: "" } as ISelection);
const journalingTypeSelection = ref('');
function journalingSelectionChanged(){
    if(journalingTypeSelection.value == ""){
        forcedIDs.value = ""
    }
}
const journalingRoutePath: string | undefined = router.options.routes
    .find((route) => route.name === 'Journaling Report')
    ?.path.split("/:")[0];
const previousPage: Ref<string | undefined> = ref(
    router.options.history.state.back?.toString()
);
const lastPage: Ref<string | undefined> = ref(
    router.options.history.state.forward?.toString()
);

log.trace("Journaling");

onBeforeUnmount(() => {
    log.trace("onBefoureUnmount() Journaling");
});
onBeforeRouteLeave((to, from, next) => {
    log.trace("onBeforeRouteLeave() Journaling");

    // If leaving to go to "Journaling Report", save journalingReports in session storage
    if (to.name === 'Journaling Report') {
        const cache: ICache = {
            Data: journalingReports.value!,
            Metadata: {
                Market: marketSelection.value,
                PageNumber: page.value,
                RowCount: rowCount.value,
                SortCols: defaultSortCols.value,
                SortOrders: defaultSortOrders.value,
                OwnerSelection: ownerSelection.value
            },
        };
        log.trace("onBeforeRouteLeave()\ncache", cache);
        sessionStorage.setItem("Journaling", JSON.stringify(cache));
    }
    next();
});
onBeforeMount(() => {
    log.trace("onBeforeMount() Journaling");
    if (
        lastPage.value?.startsWith(journalingRoutePath!) ||
        previousPage.value?.startsWith(journalingRoutePath!)
    ) {
        if ("Journaling" in sessionStorage) {
            log.debug("onBeforeMount() 'Journaling' in sessionStorage");
        }
    } else {
        log.debug("onBeforeMount() removing 'Journaling' from sessionStorage");
        sessionStorage.removeItem("Journaling");
    }
});
onMounted(async () => {
    log.trace("onMounted() Journaling");
    document.title = 'Journaling - UV';

    isLoading.value = true;
    if (!("Journaling" in sessionStorage)) {
        marketSelection.value = "GB";
        await GetContractOwners();
        await SetContractOwnerByMarket();
        await SetDefaultOwner();
    }
    else{
        cacheHolder.value = JSON.parse(sessionStorage.getItem("Journaling")!);
        marketSelection.value = cacheHolder.value?.Metadata.Market ?? "GB"
        await GetContractOwners();
        await SetContractOwnerByMarket();
    }    
    await GetJournalingReports().then(() => {
        sortJournalingReports();
    });
    
    isLoading.value = false;
});
async function GetContractOwners() {
    try {
        const res = (await contractOwnersNetworkService.get()).data
        
        unfilteredContractOwners.value = res;
    } catch (error) {
        useNotifierWithErrFormatter().error({
            errorType: "Contract Owners",
            error,
        });
    }
}

async function SetContractOwnerByMarket(){
    contractOwners.value = unfilteredContractOwners.value.filter( x => x.Market == marketSelection.value).sort((a, b) => {
                const aId = a?.OwnerId ?? "";
                const bId = b?.OwnerId ?? "";
                return bId.localeCompare(aId);
            }); 
}
async function SetDefaultOwner(){
    try{
        ownerSelection.value = contractOwners.value[0].OwnerId ?? "";
    }
    catch{
        console.warn("Contract owners were not loaded");
    }
}
async function GetJournalingReports(search?: boolean) {
    log.trace("GetJournalingReports()");

    const name = GetJournalingReports.name;
    const Class = fileName + "/" + name;

    if ("Journaling" in sessionStorage && search != true) {
        log.debug(
            "GetJournalingReports()\nLoading Journaling Reports from session storage"
        );

        const cache: ICache = cacheHolder.value ?? JSON.parse(sessionStorage.getItem("Journaling")!);
        const data: IJournalReportsItem[] = cache.Data;
        const metadata: IMetadata = cache.Metadata;
        // log.debug("GetJournalingReports()\nCACHE cache", cache);
        // log.debug("GetJournalingReports()\nCACHE data", data);

        journalingReports.value = data;
        displayedJournalingReports.value = journalingReports.value.map(ParseItems);

        marketSelection.value = (Object.keys(MarketSelection) as (keyof typeof MarketSelection)[]).find(
            m => m === metadata.Market
        ) ?? 'GB';
        ownerSelection.value = metadata.OwnerSelection!;
        scrollPosition.value = metadata.ScrollPosition!;
        page.value = metadata.PageNumber!;
        rowCount.value = metadata.RowCount!;
        defaultSortCols.value = metadata.SortCols!;
        defaultSortOrders.value = metadata.SortOrders!;
    } else {
        const urlParams: string =
            process.env.VUE_APP_JOURNALING_REPORTS_URL_PARAMS! +
            "?market=" +
            marketSelection.value +
            "&"+ "journalReference=" + journalReference.value +
            "&"+ "owner=" + ownerSelection.value;

        var Message = `Getting Journaling Reports: ${urlParams}`;

        log.debug(`${name}()\n${Message}`);
        try {
            resetErrors();
            const data = await journalingServices.getJournalingReports(urlParams);
            journalingReports.value = data;
            displayedJournalingReports.value = journalingReports.value.map(
                ParseItems
            );
            log.trace(
                "GetJournalingReports()\nAPI journalingReports",
                journalingReports.value
            );
            Logger?.LogInformation(Message, Class);
        } catch (error) {
            useNotifierWithErrFormatter().error({ errorType: 'Getting Journal Reports', error });
            tableError.value = true;
        }
    }
}

function redirectToManualJournal(){
    router.push({ path: RoutePaths["Create Manual Journal Entry"] });
}
async function updateMarketSelection() {
    sessionStorage.removeItem("Journaling")
    isLoading.value = true;
    await SetContractOwnerByMarket();
    await SetDefaultOwner();
    await GetJournalingReports();
    sortJournalingReports();
    isLoading.value = false;
}
function sortJournalingReports() {
    log.trace("sortJournalingReports()");

    displayedJournalingReports.value = displayedJournalingReports.value?.sort(
        (a, b) => new Date(b.Time).getTime() - new Date(a.Time).getTime()
    );
}
function resetErrors() {
    tableError.value = false;
}
async function RefreshJournalingTable() {
    isLoading.value = true;
    await GetJournalingReports(true).then(() => { sortJournalingReports()});
    isLoading.value = false;
}
async function ProformaJNL() {
    log.trace(ProformaJNL.name);

    const name = ProformaJNL.name;
    const Class = fileName + "/" + name;

    proformaButtonDisabled.value = true;
    resetErrors();

    lastProformaJobId.value = displayedJournalingReports.value
        ?.map((item: IJournalReportsItem) => Number(item.JobID))
        .sort((a, b) => b - a)
        .shift();
    log.debug("ProformaJNL()\nlastProformaJobId:", lastProformaJobId.value);

    log.debug("ProformaJNL() removing 'Journaling' from sessionStorage");
    sessionStorage.removeItem("Journaling");

    // log.debug("ProformaJNL()\nEmail Address:", authService.getEmailAddress());
    const body: IJournalingBody = {
        ExportJournal: false,
        UserName: authService.getEmailAddress(),
        Market: marketSelection.value,
        JournalType: journalingTypeSelection.value,
        ForcedIDs: forcedIDs.value, 
        OwnerID: ownerSelection.value
    };
    const urlParams: string = process.env.VUE_APP_PROFORMA_EXPORT_JOURNALING_URL_PARAMS!;

    isLoading.value = true;

    var Message = `Performing Proforma Journal, body: ${JSON.stringify(body)}`;

    log.debug(`name()\n${Message}`);
    await financialSystemNetworkService
        .post(body, urlParams)
        .then(async (response) => {
            log.debug("ProformaJNL() Response:", response);
            // Pull new data and load table
            
            await GetJournalingReports();
            sortJournalingReports();

            NoNewDataNotification(true);
            Logger?.LogInformation(Message, Class);
        })
        .catch((error) => {
            useNotifierWithErrFormatter().error({ errorType: 'Proforma Journal', error });
            Message = `(Error) ${Message}: ${error}`;
            log.error(Message);
            Logger?.LogError(Message, Class);
        })
        .finally(() => {
            isLoading.value = false;
            proformaButtonDisabled.value = false;
        });
}
async function ExportJNL() {
    sessionStorage.removeItem("Journaling");

    const name = ExportJNL.name;
    const Class = fileName + "/" + name;

    exportButtonDisabled.value = true;
    resetErrors();

    lastExportJobId.value = displayedJournalingReports.value
        ?.map((item: IJournalReportsItem) => Number(item.JobID))
        .sort((a, b) => b - a)
        .shift();
    log.debug("ExportJNL()\nlastExportJobId:", lastExportJobId.value);

    log.debug("ExportJNL() removing 'Journaling' from sessionStorage");
    sessionStorage.removeItem("Journaling");

    const body: IJournalingBody = {
        ExportJournal: true,
        UserName: authService.getEmailAddress(),
        Market: marketSelection.value,
        JournalType: journalingTypeSelection.value,
        ForcedIDs:forcedIDs.value, 
        OwnerID: ownerSelection.value
    };
    const urlParams: string = process.env.VUE_APP_PROFORMA_EXPORT_JOURNALING_URL_PARAMS!;

    isLoading.value = true;

    var Message = `Performing Export Journal, body: ${JSON.stringify(body)}`;

    log.debug(`name()\n${Message}`);
    await financialSystemNetworkService
        .post(body, urlParams)
        .then(async (response) => {
            log.debug("ExportJNL() Response:", response);
            // Pull new data and load table
            await GetJournalingReports();
            sortJournalingReports();
            NoNewDataNotification(false);
            Logger?.LogInformation(Message, Class);
        })
        .catch((error) => {
            useNotifierWithErrFormatter().error({ errorType: 'Export Journal', error });
            Message = `(Error) ${Message}: ${error}`;
            log.error(Message);
            Logger?.LogError(Message, Class);
        })
        .finally(() => {
            isLoading.value = false;
            exportButtonDisabled.value = false;
        });
}
function NoNewDataNotification(proforma: boolean) {
    if (proforma) {
        const latestProformaJobId = displayedJournalingReports.value
            ?.map((item: IJournalReportsItem) => Number(item.JobID))
            .sort((a, b) => b - a)
            .shift();

        log.debug(
            "NoNewDataNotification()\nlatestProformaJobId:",
            latestProformaJobId,
            "\nlastProformaJobId:",
            lastProformaJobId.value
        );
        if (latestProformaJobId === lastProformaJobId.value) 
        {    
            useNotifierWithErrFormatter().notify({msg: "No new data to perform Journaling."})
        }
    } else {
        const latestExportJobId = displayedJournalingReports.value
            ?.map((item: IJournalReportsItem) => Number(item.JobID))
            .sort((a, b) => b - a)
            .shift();

        log.debug(
            "NoNewDataNotification()\nlatestExportJobId:",
            latestExportJobId,
            "\nlastExportJobId:",
            lastExportJobId.value
        );
        if (latestExportJobId === lastExportJobId.value) {
            useNotifierWithErrFormatter().notify({msg: "No new data to perform Journaling."})
        }
    }
}
function RowClicked(row: IJournalReportsItem) {
    log.trace(RowClicked.name);
    const r = {
        name: 'Journaling Report',
        params: {
            id: row.JobID,
        },
        query: {
            JournalType: row.JournalType,
            Status: row.Status,
        },
    };
    // Open in current tab
    router.push(r);
}
</script>
<style lang="scss" scoped>
@import "@/assets/styles/centre-button.scss";

:deep(.flex-row-centered ) {
    display:flex;
    align-items: flex-end;
}

.centre-button-journal-reference{
    @extend .centre-button;
    padding: 0px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.centre-button-journal-reference:hover .selection-form-btn {
    background-color: var(--primary-color);
}

.selection-form-btn {
    border-color: var(--primary-color-dark);
    font-weight: bold;
    background-color: var(--primary-color-lighter);
    color: blue;
    cursor: pointer;

    &:hover {
        background-color: var(--primary-color);
        color: white;
    }
}
::v-deep(#market) {
    min-width: unset !important;
    width: 6rem;
}
::v-deep(#journal-ref-id) {
    min-width: unset !important;
    width: 14rem;
}
::v-deep(#owner) {
    min-width: unset !important;
    width: 6rem;
}
</style>